import { useCallback, useEffect, useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { toolbarPlugin, ToolbarProps, ToolbarSlot, TransformToolbarSlot } from '@react-pdf-viewer/toolbar';

import { useBreakpoints, Breakpoints } from 'app/hooks';
import { Box, Flex, Loading } from 'components';
import { SendFeedbackProps, useContents } from 'features/contents';
import { debounce, request } from 'app/utils';
import { useSchool } from 'features/school';

import PdfTimeline from './pdfTimeline';
import Comments, { useComments } from 'features/comments';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';

// const PDF_VIEWER_KEY = process.env.REACT_APP_PDF_VIEWER_KEY;

interface WindowSize {
  innerWidth: number;
  innerHeight: number;
  videoWidth: number;
  videoHeight: number;
}

const PdfWrapper = styled(Flex)`
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MessagesTabs = styled(Flex)`
  flex-shrink: 0;
  height: 60px;
`;

const MessagesButton = styled.button`
  display: flex;
  align-items: center;
  position: relative;
  border: 0;
  background: none;
  color: white;
  text-transform: uppercase;
  font-size: 18px;
  padding: 0 40px;

  span {
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.colors.gray_600};
    font-size: 10px;
    margin-left: 10px;
    padding: 0 5px;
    border-radius: 4px;
  }

  ${(props: any) => {
    if (props['data-active']) {
      return `
        &::after {
          content: '';
          width: 100%;
          height: 4px;
          position: absolute;
          left: 0;
          z-index: 10;
          bottom: 0;
          background: ${props.theme.colors.primary_400};
        }
      `;
    }
  }}
`;

interface ContentPdfProps {
  sendFeedback: (props: SendFeedbackProps) => void;
}

const ContentPdf = ({ sendFeedback }: ContentPdfProps) => {
  const breakpoint = useBreakpoints();
  const [windowSize, setWindowSize] = useState<WindowSize | null>(null);
  const { productSlug, contentUuid } = useParams();

  const { contentDetails, clearContentDetails } = useContents();
  const { apiGatewayUrl } = useSchool();
  const {
    comments,
    commentsConfig: { enableComments },
  } = useComments();

  const token = Cookies.get('token');

  useEffect(() => {
    if (contentDetails?.contentUuid) {
      request({
        url: `/api/v1/School/AssessContentExternalDocumentRead`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          contentUuid: contentDetails.contentUuid,
          productClassUuid: contentDetails.productClassUuid,
        },
      });
    }
  }, [contentDetails]);

  useEffect(() => {
    return () => {
      clearContentDetails();
    };
  }, []);

  const checkOnResize = useCallback(
    debounce(() => {
      if (breakpoint) setWindowSize(getWindowSize(breakpoint, 2));
    }, 150),
    [breakpoint]
  );

  // const fetchDocument = useCallback(() => {
  //   request({
  //     url: `/api/v1/school/ProductContentItemExternalDocument/${productSlug}/${contentUuid}`,
  //     method: 'get',
  //     baseURL: apiGatewayUrl,
  //   });
  // }, [productSlug, contentUuid]);

  // const defaultLayoutPluginInstance = defaultLayoutPlugin({
  //   // sidebarTabs: (defaultTabs) =>
  //   //   defaultTabs.concat({
  //   //     content: <div style={{ textAlign: 'center', width: '100%' }}>Notes are listed here</div>,
  //   //     icon: (
  //   //       <Icon size={16}>
  //   //         <path d="M23.5,17a1,1,0,0,1-1,1h-11l-4,4V18h-6a1,1,0,0,1-1-1V3a1,1,0,0,1,1-1h21a1,1,0,0,1,1,1Z" />
  //   //         <path d="M5.5 12L18.5 12" />
  //   //         <path d="M5.5 7L18.5 7" />
  //   //       </Icon>
  //   //     ),
  //   //     title: 'Notes',
  //   //   }),
  // });

  const toolbarPluginInstance = toolbarPlugin({});

  const transform: TransformToolbarSlot = (slot: ToolbarSlot) => ({
    ...slot,

    Open: () => <></>,
    OpenMenuItem: () => <></>,

    Download: () => <></>,
    DownloadMenuItem: () => <></>,

    Print: () => <></>,
    PrintMenuItem: () => <></>,

    EnterFullScreen: () => <></>,
    EnterFullScreenMenuItem: () => <></>,

    SwitchTheme: () => <></>,
    SwitchThemeMenuItem: () => <></>,

    CurrentPageInput: () => <></>,
    CurrentPageLabel: () => <></>,
    CurrentScale: () => <></>,
    GoToFirstPage: () => <></>,
    GoToFirstPageMenuItem: () => <></>,
    GoToLastPage: () => <></>,
    GoToLastPageMenuItem: () => <></>,
    GoToNextPage: () => <></>,
    GoToNextPageMenuItem: () => <></>,
    GoToPreviousPage: () => <></>,
    GoToPreviousPageMenuItem: () => <></>,
    NumberOfPages: () => <></>,
    Rotate: () => <></>,
    // RotateBackwardMenuItem: () => <></>,
    // RotateForwardMenuItem: () => <></>,
    Search: () => <></>,
    ShowSearchPopover: () => <></>,
    ShowProperties: () => <></>,
    ShowPropertiesMenuItem: () => <></>,
    SwitchScrollMode: () => <></>,
    SwitchScrollModeMenuItem: () => <></>,
    SwitchSelectionMode: () => <></>,
    SwitchSelectionModeMenuItem: () => <></>,
    SwitchViewMode: () => <></>,
    // SwitchViewModeMenuItem: () => <></>,
    // Zoom: () => <></>,
    // ZoomIn: () => <></>,
    // ZoomInMenuItem: () => <></>,
    // ZoomOut: () => <></>,
    // ZoomOutMenuItem: () => <></>,
  });

  const renderToolbar = (Toolbar: (props: ToolbarProps) => React.ReactElement) => <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>;

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar,
    sidebarTabs: (defaultTabs) => [],
  });

  const { renderDefaultToolbar } = defaultLayoutPluginInstance.toolbarPluginInstance;

  useEffect(() => {
    checkOnResize();

    window.addEventListener('resize', checkOnResize);

    return () => {
      window.removeEventListener('resize', checkOnResize);
    };
  }, [breakpoint]);

  // useEffect(() => {
  //   fetchDocument();
  // }, [productSlug, contentUuid]);

  if (!contentDetails?.externalDocument || !windowSize) return <Loading label="Por favor, aguarde." />;

  const orientation = windowSize.innerWidth > windowSize.innerHeight ? 'portrait' : 'landscape';

  return (
    <Flex flexGrow={1} flexDirection={{ m: 'column', d: 'row' }} flexWrap="nowrap" bg="videoContentBg">
      <PdfWrapper
        data-videowrapper
        flexGrow={{ m: 0, d: 1 }}
        height={{ d: 'calc(100vh - 81px)' }}
        bg="dark_400"
        flexDirection="column"
        flexWrap="nowrap"
        overflowY={breakpoint === 'desktop' ? 'scroll' : 'inherit'}
        justifyContent="flex-start"
      >
        <Box>
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
            <Viewer
              fileUrl={`${apiGatewayUrl}/api/v1/school/ProductContentItemExternalDocument/${productSlug}/${contentUuid}`}
              plugins={[defaultLayoutPluginInstance, toolbarPluginInstance]}
              httpHeaders={{
                Authorization: `Bearer ${token}`,
              }}
              // localization={pt_PT as unknown as LocalizationMap}
            />
          </Worker>
        </Box>

        {breakpoint === 'desktop' && (
          <>
            {enableComments && (
              <>
                <MessagesTabs flexGrow={0} bg="dark_400" px={{ default: 6 }}>
                  <MessagesButton data-active>
                    Comentários<span>{comments?.length}</span>
                  </MessagesButton>

                  {/* <MessagesButton>Anotações</MessagesButton> */}
                </MessagesTabs>
              </>
            )}
            <Comments />
          </>
        )}
      </PdfWrapper>

      <PdfTimeline orientation={orientation} windowSize={windowSize} breakpoint={breakpoint} sendFeedback={sendFeedback} />
    </Flex>
  );
};

export default ContentPdf;

const getWindowSize = (breakpoint: Breakpoints, from = 0): WindowSize => {
  const { innerWidth, innerHeight } = window;

  const videoWidth = breakpoint === 'desktop' ? Math.ceil(innerWidth - 425) : innerWidth;
  const videoHeight = Math.ceil(videoWidth / 1.775347912524851);

  return { innerWidth, innerHeight, videoWidth, videoHeight };
};
